// Card de um agendamento do evento (§4.3): direção (ANTES/DEPOIS) +
// dias, switch de enable (write otimista), templates por canal e
// ações editar/remover (AlertDialog no destrutivo).
import { PenIcon, Trash2Icon } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { cn } from "@/lib/utils"
import type {
  NotificationEventSchedulerType,
  NotificationOriginTransactionType,
  NotificationWhiteLabelChannelProviderType,
} from "@/types/notification-center"

import {
  useDeleteEventScheduler,
  useUpdateEventScheduler,
} from "../configuration-mutations"
import { SchedulerFormDialog } from "./scheduler-form-dialog"

const detailPrefix =
  "pages.notificationCenter.configurationsDetailPage"

// Payload de escrita no shape do contrato, a partir do estado atual.
const toWritePayload = (
  scheduler: NotificationEventSchedulerType,
  enabled: boolean
) => ({
  direction: scheduler.direction,
  days: scheduler.days,
  enabled,
  templates: scheduler.templates.map((template) => ({
    channel: template.channel,
    templateUuid: template.uuid,
  })),
})

type EventSchedulerCardProps = {
  whiteLabelId: string
  eventName: string
  // Linha do card do evento (ORDER/SUBSCRIPTION); undefined = antigo.
  transactionType?: NotificationOriginTransactionType
  eventLabel: string
  scheduler: NotificationEventSchedulerType
  activeChannels: NotificationWhiteLabelChannelProviderType[]
}

export const EventSchedulerCard = ({
  whiteLabelId,
  eventName,
  transactionType,
  eventLabel,
  scheduler,
  activeChannels,
}: EventSchedulerCardProps) => {
  const { t } = useTranslation()
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] =
    useState(false)
  const updateScheduler =
    useUpdateEventScheduler(whiteLabelId)
  const deleteScheduler =
    useDeleteEventScheduler(whiteLabelId)

  // Ex.: "3 dias antes" / "1 dia depois" (tabular-nums no número).
  const scheduleLabel = `${scheduler.days} ${t(
    scheduler.days === 1
      ? `${detailPrefix}.day`
      : `${detailPrefix}.days`
  )} ${t(
    scheduler.direction === "BEFORE"
      ? `${detailPrefix}.before`
      : `${detailPrefix}.after`
  )}`

  return (
    <li className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Switch
            checked={scheduler.enabled}
            onCheckedChange={(checked) =>
              updateScheduler.mutate({
                eventName,
                transactionType,
                schedulerUuid: scheduler.uuid,
                payload: toWritePayload(scheduler, checked),
                optimisticTemplates: scheduler.templates,
              })
            }
            aria-label={t(
              `${detailPrefix}.toggleScheduler`,
              { schedule: scheduleLabel }
            )}
          />
          <span
            className={cn(
              "text-sm font-medium tabular-nums",
              !scheduler.enabled && "text-muted-foreground"
            )}
          >
            {scheduleLabel}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t(`${detailPrefix}.editScheduler`, {
              schedule: scheduleLabel,
            })}
            onClick={() => setIsEditOpen(true)}
          >
            <PenIcon
              className="size-4"
              aria-hidden="true"
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t(
              `${detailPrefix}.removeScheduler`,
              { schedule: scheduleLabel }
            )}
            onClick={() => setIsRemoveDialogOpen(true)}
          >
            <Trash2Icon
              className="size-4 text-red-600"
              aria-hidden="true"
            />
          </Button>
        </div>
      </div>

      {scheduler.templates.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {scheduler.templates.map((template) => (
            <li
              key={`${template.channel}-${template.uuid}`}
            >
              <Badge
                variant="outline"
                className="rounded-full font-normal"
              >
                {template.channel}: {template.name}
                <span className="ml-1 tabular-nums text-muted-foreground">
                  @v{template.version}
                </span>
              </Badge>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t(`${detailPrefix}.schedulerNoTemplates`)}
        </p>
      )}

      {/* Remoção é destrutiva → AlertDialog. */}
      <AlertDialog
        open={isRemoveDialogOpen}
        onOpenChange={setIsRemoveDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${detailPrefix}.removeDialog.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${detailPrefix}.removeDialog.description`,
                {
                  name: eventLabel,
                  schedule: scheduleLabel,
                }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${detailPrefix}.removeDialog.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() =>
                deleteScheduler.mutate({
                  eventName,
                  transactionType,
                  schedulerUuid: scheduler.uuid,
                })
              }
            >
              {t(`${detailPrefix}.removeDialog.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isEditOpen && (
        <SchedulerFormDialog
          whiteLabelId={whiteLabelId}
          eventName={eventName}
          transactionType={transactionType}
          activeChannels={activeChannels}
          scheduler={scheduler}
          onClose={() => setIsEditOpen(false)}
        />
      )}
    </li>
  )
}
